import {
  Faro,
  getWebInstrumentations,
  initializeFaro,
  LogLevel,
  TransportItem,
  TransportItemType,
} from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';

const APP_NAME = 'ce-frontend';
const UNKNOWN_BUNDLE = 'unknown';
const HANDLED_ERROR_FALLBACK = 'Unknown handled error';
const MAX_HANDLED_CONTEXT_LENGTH = 128;
const MAX_SIGNAL_ATTRIBUTES = 20;
const SIGNAL_ID_PATTERN = /^[a-z][a-z0-9_.-]{0,127}$/;
const SIGNAL_ATTRIBUTE_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;
// Values lose their labels before string sanitation; reject explicitly sensitive keys here.
const FORBIDDEN_ATTRIBUTE_PATTERN = /(authorization|api_?key|body|cookie|credential|header|password|secret|token|payload|payment|card|cvv|cvc|iban|user_?input)/i;
const THIRD_PARTY_URLS = [
  /https?:\/\/static\.katalon\.com\//i,
  /https?:\/\/api\.ip\.sb\//i,
  /https?:\/\/cdn\.segment\.com\//i,
  /https?:\/\/[^/]*google-analytics\.com\//i,
  /https?:\/\/[^/]*googletagmanager\.com\//i,
  /https?:\/\/js\.stripe\.com\//i,
  /https?:\/\/[^/]*posthog\.com\//i,
  /https?:\/\/[^/]*launchdarkly\.(?:com|us)\//i,
];
const LOCAL_LOOPBACK_ORIGIN = String.raw`http:\/\/(?:localhost|127\.0\.0\.1)(?::\d+)?`;
const ABSOLUTE_URL_PATTERN = /https?:\/\/[^\s<>"']+/gi;
const RELATIVE_URL_PATTERN = /(^|[^A-Z0-9@._~-])((?:(?:\/{1,2}|\.\.?\/)|[A-Z0-9._~-]+\/)[^\s<>"']*[?#][^\s<>"']*)/gi;
const EMAIL_PATTERN = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi;
const BEARER_TOKEN_PATTERN = /\bBearer\s+[A-Z0-9._~+/-]+=*/gi;
// Ambiguous business words require explicit key/value syntax. Strong secret labels
// also cover console printf/space-separated values. Values may be multiline JSON:
// conservatively discard their remainder, but sanitize error frames separately below.
const SENSITIVE_KEY_VALUE_PATTERN = /\b(?:(?:authorization|(?:access|refresh|id)[_-]?token|token|api[_-]?key|password|secret|cookies?|credentials?|(?:request|response)?[_-]?body|payload|payment|card(?:[_-]?number)?|cvv|cvc|iban|user[_ -]?input)\b["']?\s*[:=]|(?:authorization|(?:access|refresh|id)[_-]?token|api[_-]?key|password|secret|cookies?|credentials?|card[_-]?number|cvv|cvc|iban|user[_ -]?input)\b["']?\s+)[\s\S]*/gi;
const CARD_NUMBER_PATTERN = /\b[3-6](?:[ -]?\d){12,18}\b/g;

export type FrontendBundle = 'master-app-ui' | 'subscription-ui' | 'unknown';
export type FrontendLogLevel = 'debug' | 'info' | 'log' | 'warn' | 'error';
export type FrontendSignalValue = string | number | boolean;

export interface FrontendSignalContext {
  attributes?: Record<string, FrontendSignalValue | undefined>;
  bundle: FrontendBundle;
  component?: string;
}

export interface HandledErrorContext {
  bundle: FrontendBundle;
  component?: string;
  operation: string;
}

export type HandledErrorReporter = (error: unknown, context: HandledErrorContext) => void;
export type FrontendLogReporter = (
  level: FrontendLogLevel,
  event: string,
  context: FrontendSignalContext,
) => void;
export type FrontendStatusReporter = (status: string, context: FrontendSignalContext) => void;

export interface FrontendObservabilityConfig {
  bundleId: string;
  collectorUrl: string;
  environment: string;
  version: string;
}

export interface FrontendObservabilityIdentity {
  accountId?: number;
  userId?: number;
}

type FaroFactory = typeof initializeFaro;

const FARO_LOG_LEVELS: Record<FrontendLogLevel, LogLevel> = {
  debug: LogLevel.DEBUG,
  error: LogLevel.ERROR,
  info: LogLevel.INFO,
  log: LogLevel.LOG,
  warn: LogLevel.WARN,
};

let frontendObservability: Faro | undefined;
let resourceErrorListener: ((event: Event) => void) | undefined;
let appliedIdentities = new WeakMap<Faro, FrontendObservabilityIdentity>();

const trim = (value?: string) => value?.trim() ?? '';

const getConfiguredServiceUrls = (): Array<string | undefined> => [
  process.env.REACT_APP_API_URL,
  process.env.REACT_APP_API_URL_V2,
  process.env.REACT_APP_K1_API_URL,
  process.env.REACT_APP_K1_API_V2_URL,
  process.env.REACT_APP_MKT_API_URL,
  process.env.REACT_APP_KEYCLOAK_URL,
  process.env.REACT_APP_PDP_SERVICE_URL,
];

const escapeRegExp = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export const getFrontendRequestIgnoreUrls = (
  environment: string,
  currentOrigin: string,
  configuredServiceUrls: Array<string | undefined> = getConfiguredServiceUrls(),
): Array<string | RegExp> => {
  const approvedOrigins = new Set<string>();

  [currentOrigin, ...configuredServiceUrls].forEach(value => {
    if (!value) {
      return;
    }

    try {
      const url = new URL(value, currentOrigin);
      if (url.protocol === 'http:' || url.protocol === 'https:') {
        approvedOrigins.add(url.origin);
      }
    } catch {
      // Invalid configured URLs must fail closed for telemetry without affecting CE requests.
    }
  });

  const approvedOriginPatterns = Array.from(approvedOrigins).map(escapeRegExp);
  if (environment === 'local') {
    approvedOriginPatterns.push(LOCAL_LOOPBACK_ORIGIN);
  }

  const approvedAlternation = approvedOriginPatterns.length > 0
    ? approvedOriginPatterns.join('|')
    : '(?!)';
  const unknownAbsoluteOrigin = new RegExp(
    `^(?!(?:${approvedAlternation})(?:/|$))https?:\\/\\/`,
    'i',
  );

  return [
    ...THIRD_PARTY_URLS,
    /^\/\//,
    /^(?!https?:\/\/)[a-z][a-z0-9+.-]*:/i,
    unknownAbsoluteOrigin,
  ];
};

export const getFrontendObservabilityConfig = (): FrontendObservabilityConfig => {
  const version = trim(process.env.REACT_APP_FARO_APP_VERSION) || UNKNOWN_BUNDLE;

  return {
    bundleId: trim(process.env.REACT_APP_FARO_BUNDLE_ID) || version,
    collectorUrl: trim(process.env.REACT_APP_FARO_COLLECTOR_URL),
    environment: trim(process.env.REACT_APP_FARO_ENVIRONMENT)
      || process.env.NODE_ENV
      || UNKNOWN_BUNDLE,
    version,
  };
};

const isThirdPartyUrl = (url: string) => THIRD_PARTY_URLS.some(pattern => pattern.test(url));

const stripQueryAndFragment = (url: string): string => {
  const queryIndex = url.indexOf('?');
  const fragmentIndex = url.indexOf('#');
  const indices = [queryIndex, fragmentIndex].filter(index => index >= 0);

  return indices.length === 0 ? url : url.slice(0, Math.min(...indices));
};

export const sanitizeTelemetryString = (value: string): string => value
  .replace(ABSOLUTE_URL_PATTERN, stripQueryAndFragment)
  .replace(
    RELATIVE_URL_PATTERN,
    (_match: string, prefix: string, url: string) => `${prefix}${stripQueryAndFragment(url)}`,
  )
  .replace(EMAIL_PATTERN, '[redacted-email]')
  .replace(BEARER_TOKEN_PATTERN, '[redacted-authorization]')
  .replace(SENSITIVE_KEY_VALUE_PATTERN, '[redacted-sensitive]');

const serializeConsoleArgs = (args: unknown[]): string => {
  try {
    // Never inspect object properties or call application-controlled getters/toJSON/toString.
    // Sanitize after joining so console.info('password=', value) is also covered.
    return sanitizeTelemetryString(args.map(value => {
      if (value === null) {
        return 'null';
      }
      switch (typeof value) {
        case 'string':
        case 'number':
        case 'boolean':
        case 'bigint':
        case 'undefined':
          return String(value);
        case 'object':
          return Array.isArray(value) ? '[array]' : '[object]';
        default:
          return `[${typeof value}]`;
      }
    }).join(' '));
  } catch {
    return '[unserializable]';
  }
};

const sanitizeHandledContextValue = (value: string): string => (
  sanitizeTelemetryString(value).slice(0, MAX_HANDLED_CONTEXT_LENGTH)
);

const sanitizeSignalContext = (context: FrontendSignalContext): Record<string, string> => {
  const sanitized: Record<string, string> = { bundle: context.bundle };

  if (context.component) {
    sanitized.component = sanitizeHandledContextValue(context.component);
  }

  Object.entries(context.attributes ?? {})
    .slice(0, MAX_SIGNAL_ATTRIBUTES)
    .forEach(([key, value]) => {
      if (
        value !== undefined
        && SIGNAL_ATTRIBUTE_PATTERN.test(key)
        && !FORBIDDEN_ATTRIBUTE_PATTERN.test(key)
      ) {
        sanitized[key] = sanitizeHandledContextValue(String(value));
      }
    });

  return sanitized;
};

const normalizeHandledError = (value: unknown): Error => {
  if (!(value instanceof Error)) {
    return new Error(typeof value === 'string'
      ? sanitizeTelemetryString(value)
      : HANDLED_ERROR_FALLBACK);
  }

  const normalized = new Error(sanitizeTelemetryString(value.message));
  normalized.name = sanitizeTelemetryString(value.name);
  if (value.stack) {
    // Keep the entire message preamble together so multiline secret values cannot
    // escape redaction. V8 and Firefox/Safari frame lines are sanitized independently.
    const lines = value.stack.split('\n');
    const frameIndex = lines.findIndex(line => /^\s*at\s+|@(?:https?:\/\/|\/)/.test(line));
    normalized.stack = frameIndex < 0 ? sanitizeTelemetryString(value.stack) : [
      sanitizeTelemetryString(lines.slice(0, frameIndex).join('\n')),
      ...lines.slice(frameIndex).map(line => sanitizeTelemetryString(line.replace(
        /(https?:\/\/.*?)(:\d+:\d+)(\)?[ \t]*$)/,
        (_match, url: string, location: string, ending: string) => (
          `${sanitizeTelemetryString(url)}${location}${ending}`
        ),
      ))),
    ].join('\n');
  }

  return normalized;
};

const sanitizeTelemetryValue = (value: unknown): void => {
  if (typeof value !== 'object' || value === null) {
    return;
  }

  const pending = [value];
  const seen = new WeakSet<object>();

  while (pending.length > 0) {
    const current = pending.pop();

    if (!current || seen.has(current)) {
      continue;
    }

    seen.add(current);

    Object.entries(current).forEach(([key, nestedValue]) => {
      if (typeof nestedValue === 'string') {
        (current as Record<string, unknown>)[key] = sanitizeTelemetryString(nestedValue);
      } else if (typeof nestedValue === 'object' && nestedValue !== null) {
        pending.push(nestedValue);
      }
    });
  }
};

export const buildSubscriptionBundlePattern = (
  microappUrl = trim(process.env.REACT_APP_SUBSCRIPTIONS_MANAGEMENT_MICROAPP_URL),
): RegExp => {
  const microappPath = microappUrl
    .replace(/^https?:\/\/[^/]+/i, '')
    .replace(/^\/+|\/+$/g, '');
  const knownPrefixes = ['sub-mgmt', 'subscription-mgmt'];

  if (microappPath.length > 0 && !knownPrefixes.includes(microappPath.toLowerCase())) {
    knownPrefixes.push(escapeRegExp(microappPath));
  }

  return new RegExp(`/(?:${knownPrefixes.join('|')})/`, 'i');
};

const SUBSCRIPTION_BUNDLE_PATTERN = buildSubscriptionBundlePattern();

export const identifyFrontendBundle = (
  urls: string[],
  subscriptionBundlePattern: RegExp = SUBSCRIPTION_BUNDLE_PATTERN,
): FrontendBundle => {
  if (urls.some(url => subscriptionBundlePattern.test(url))) {
    return 'subscription-ui';
  }

  if (urls.some(url => !isThirdPartyUrl(url) && /\/(?:assets|static\/(?:css|js))\//i.test(url))) {
    return 'master-app-ui';
  }

  return UNKNOWN_BUNDLE;
};

const installResourceErrorListener = () => {
  if (resourceErrorListener) {
    return;
  }

  const reportedTargets = new WeakSet<EventTarget>();
  resourceErrorListener = (event: Event) => {
    const { target } = event;

    if (!(target instanceof Element) || reportedTargets.has(target)) {
      return;
    }

    const isStylesheet = target.tagName.toLowerCase() === 'link'
      && (target as HTMLLinkElement).relList.contains('stylesheet');
    if (!isStylesheet) {
      return;
    }

    const rawUrl = (target as HTMLLinkElement).getAttribute('href');
    if (!rawUrl) {
      return;
    }

    try {
      const resourceUrl = new URL(rawUrl, window.location.href);
      if (resourceUrl.origin !== window.location.origin) {
        return;
      }

      reportedTargets.add(target);
      reportHandledError(
        Error(`Failed to load first-party stylesheet: ${sanitizeTelemetryString(resourceUrl.href)}`),
        {
          bundle: identifyFrontendBundle([resourceUrl.href]),
          component: 'resource-loader',
          operation: 'load-stylesheet',
        },
      );
    } catch {
      // Invalid resource URLs must not affect CE behavior.
    }
  };

  window.addEventListener('error', resourceErrorListener, true);
};

const enrichOrDropTelemetry = (item: TransportItem): TransportItem | null => {
  sanitizeTelemetryValue(item);

  if (item.type === TransportItemType.LOG) {
    const payload = item.payload as { message: string };
    // Restrict PAN-like number filtering to message text, not trace IDs or nanosecond timestamps.
    payload.message = payload.message.replace(CARD_NUMBER_PATTERN, '[redacted-payment]');
  }

  if (item.type !== TransportItemType.EXCEPTION) {
    return item;
  }

  const payload = item.payload as {
    context?: Record<string, string>;
    stacktrace?: { frames?: Array<{ filename?: string }> };
    value?: string;
  };
  if (payload.value) {
    payload.value = payload.value.replace(CARD_NUMBER_PATTERN, '[redacted-payment]');
  }
  const frameUrls = (payload.stacktrace?.frames ?? [])
    .map(frame => frame.filename ?? '')
    .filter(Boolean);

  if (frameUrls.length > 0 && frameUrls.every(isThirdPartyUrl)) {
    return null;
  }

  payload.context = {
    ...payload.context,
    bundle: ['master-app-ui', 'subscription-ui', UNKNOWN_BUNDLE]
      .includes(payload.context?.bundle ?? '')
      ? payload.context?.bundle ?? UNKNOWN_BUNDLE
      : identifyFrontendBundle(frameUrls),
  };

  return item;
};

export const initializeFrontendObservability = (
  config: FrontendObservabilityConfig = getFrontendObservabilityConfig(),
  factory: FaroFactory = initializeFaro,
): Faro | undefined => {
  if (frontendObservability || typeof window === 'undefined' || config.collectorUrl.length === 0) {
    return frontendObservability;
  }

  try {
    frontendObservability = factory({
      app: {
        bundleId: config.bundleId,
        environment: config.environment,
        name: APP_NAME,
        version: config.version,
      },
      beforeSend: item => {
        try {
          // console.error(Error) bypasses the SDK error serializer; filter its payload too.
          return enrichOrDropTelemetry(item);
        } catch {
          // A filter failure drops telemetry, never CE behavior or the privacy boundary.
          return null;
        }
      },
      consoleInstrumentation: {
        errorSerializer: serializeConsoleArgs,
      },
      dedupe: true,
      experimental: {
        trackNavigation: true,
      },
      ignoreUrls: getFrontendRequestIgnoreUrls(config.environment, window.location.origin),
      instrumentations: [
        ...getWebInstrumentations(),
        new TracingInstrumentation(),
      ],
      isolate: true,
      logArgsSerializer: serializeConsoleArgs,
      pageTracking: {
        generatePageId: location => location.pathname,
      },
      url: config.collectorUrl,
    });
  } catch {
    frontendObservability = undefined;
    return undefined;
  }

  try {
    installResourceErrorListener();
  } catch {
    // Optional observability hooks must not affect CE startup.
  }
  return frontendObservability;
};

export const reportFrontendLog: FrontendLogReporter = (level, event, context) => {
  if (!SIGNAL_ID_PATTERN.test(event)) {
    return;
  }

  if (!frontendObservability) {
    try {
      // Preserve browser diagnostics when optional telemetry is disabled.
      // eslint-disable-next-line no-console
      console[level](event);
    } catch { /* Console overrides must not affect application behavior. */ }
    return;
  }

  try {
    frontendObservability.api.pushLog([event], {
      context: sanitizeSignalContext(context),
      level: FARO_LOG_LEVELS[level],
      skipDedupe: true,
    });
  } catch {
    // Observability must never affect CE behavior.
  }
};

export const reportFrontendStatus: FrontendStatusReporter = (status, context) => {
  if (!frontendObservability || !SIGNAL_ID_PATTERN.test(status)) {
    return;
  }

  try {
    frontendObservability.api.pushEvent('ce.frontend.status', {
      ...sanitizeSignalContext(context),
      status,
    }, undefined, { skipDedupe: true });
  } catch {
    // Observability must never affect CE behavior.
  }
};

export const reportHandledError: HandledErrorReporter = (error, context) => {
  if (!frontendObservability) {
    try {
      // Preserve the original console.error behavior when collection is disabled.
      // eslint-disable-next-line no-console
      console.error(error);
    } catch { /* Console overrides must not affect application behavior. */ }
    return;
  }

  try {
    const handledContext: Record<string, string> = {
      bundle: context.bundle,
      operation: sanitizeHandledContextValue(context.operation),
    };

    if (context.component) {
      handledContext.component = sanitizeHandledContextValue(context.component);
    }

    frontendObservability.api.pushError(normalizeHandledError(error), {
      context: handledContext,
      skipDedupe: true,
      type: 'HandledError',
    });
  } catch {
    // Observability must never affect CE behavior.
  }
};

export const updateFrontendObservabilityIdentity = (
  faro: Faro | undefined,
  identity: FrontendObservabilityIdentity,
) => {
  if (!faro) {
    return;
  }
  try {
    const next: FrontendObservabilityIdentity = identity.userId === undefined
      ? {}
      : { userId: identity.userId, accountId: identity.accountId };
    const previous = appliedIdentities.get(faro);
    if (previous
      && previous.userId === next.userId
      && previous.accountId === next.accountId) {
      return;
    }

    if (next.userId === undefined) {
      faro.api.resetUser();
    } else {
      faro.api.setUser({
        id: String(next.userId),
        attributes: next.accountId === undefined
          ? {}
          : { account_id: String(next.accountId) },
      });
    }
    appliedIdentities.set(faro, next);
  } catch {
    // An SDK listener can throw after metadata changes; cached identity is now uncertain.
    appliedIdentities.delete(faro);
    // Identity enrichment must never affect CE behavior; retry on the next call.
  }
};

export const resetFrontendObservabilityForTest = () => {
  if (resourceErrorListener) {
    window.removeEventListener('error', resourceErrorListener, true);
    resourceErrorListener = undefined;
  }
  frontendObservability = undefined;
  appliedIdentities = new WeakMap<Faro, FrontendObservabilityIdentity>();
};
